import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { AuditCategoryEnum, AuditLogEntity, UserEntity } from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaUserAvatarComponent } from '@shared/components';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { I18nService } from 'src/app/shared/services/i18n.service';

/**
 * Um item da timeline de histórico do documento (layout vertical do drawer).
 * Espelha a lógica de `audit-event-row` (categoria, ação humanizada, diff), mas
 * com apresentação de timeline em vez de linha de tabela.
 */
@Component({
    selector: 'document-history-item',
    templateUrl: 'document-history-item.component.html',
    styleUrl: 'document-history-item.component.scss',
    imports: [CommonModule, TranslatePipe, RaUserAvatarComponent, RaIconComponent],
})
export class DocumentHistoryItemComponent {
    public event = input.required<AuditLogEntity>();
    public openDiff = output<AuditLogEntity>();

    private _translate = inject(TranslateService);
    private _i18n = inject(I18nService);

    /** Ator tipado como UserEntity para o `ra-user-avatar`. */
    public actorUser = computed(() => this.event().user as unknown as UserEntity);

    /** Slug da categoria (create/update/delete/auth) para cor/i18n. */
    public categoryClass = computed<string>(() => {
        switch (this.event().category) {
            case AuditCategoryEnum.CREATE:
                return 'create';
            case AuditCategoryEnum.DELETE:
                return 'delete';
            case AuditCategoryEnum.AUTH:
                return 'auth';
            default:
                return 'update';
        }
    });

    /** A edição tem diff detalhável (metadata.before/after). */
    public hasDiff = computed<boolean>(() => {
        const meta = this.event().metadata as { before?: unknown; after?: unknown } | undefined;
        return !!meta && (meta.before !== undefined || meta.after !== undefined);
    });

    /** Ações de campo cujo rótulo varia por tipo → prefixo do mapa i18n. */
    private readonly _fieldActionPrefix: Record<string, string> = {
        'document.create-field': 'audit.createField',
        'document.remove-fields': 'audit.removeField',
        'document.update-text': 'audit.updateText',
    };

    /**
     * Ação humanizada (ex.: `document.update-text` → "Atualização de texto").
     * Para criar/remover/editar campo, usa o rótulo específico do tipo
     * (texto/imagem/grupo/…) quando o `metadata.fieldType` está presente.
     */
    public humanizedDetail = computed<string>(() => {
        const ev = this.event();
        const action = ev.action ?? '';

        // Rótulo por tipo de campo (Campo de texto criado / Imagem removida / Título editado…).
        const prefix = this._fieldActionPrefix[action];
        if (prefix) {
            const fieldType = (ev.metadata as { fieldType?: string } | undefined)?.fieldType;
            if (fieldType) {
                const typeKey = `${prefix}.${fieldType}`;
                const typeLabel = this._i18n.t(typeKey);
                if (typeLabel && typeLabel !== typeKey) return typeLabel;
            }
        }

        const key = `audit.actions.${action.replace(/\./g, '_')}`;
        const label = this._i18n.t(key);
        if (label && label !== key) return label;

        const tail = action.includes('.') ? action.slice(action.indexOf('.') + 1) : action;
        const text = tail.replace(/[-_]/g, ' ').trim();
        return text ? text.charAt(0).toUpperCase() + text.slice(1) : action;
    });

    public onDiff(event: MouseEvent): void {
        event.stopPropagation();
        this.openDiff.emit(this.event());
    }
}
